import { useSetAtom } from "jotai";
import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useParams, useNavigate } from "react-router-dom";

import useUser from "../auth/useUser";
import { workspaceIdAtom } from "../../../atoms/WorkspaceAtoms";
import useCloudPermissions from "../../../hooks/useCloudPermissions";
import WorkspaceAPI from "../../api/WorkspaceAPI";
import { handleWorkspacesUpdate } from "../../../utils/WorkspaceUtils";
import { newToastEvent } from "../../../utils/ToastUtil";
import {
  callFunctionIfExists,
  getErrorMessage,
} from "../../../utils/GeneralUtils";
import TenantAPI from "../../api/TenantAPI";

const useAddWorkspace = () => {
  const setWorkspaceID = useSetAtom(workspaceIdAtom);
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { tenantID }: any = useParams();
  const { cPermissions } = useCloudPermissions();
  const { user } = useUser();
  const [loading, setLoading] = useState(false);

  const addWorkspace = useMutation({
    mutationFn: ({ payload }: { payload: any; onSuccess?: () => void }) => {
      if (!cPermissions.cloud_create_workspace) {
        return Promise.reject(
          "User does not have permission to create a workspace"
        );
      }
      setLoading(true);
      return WorkspaceAPI.createWorkspace(payload).then((data) => {
        const result = data.data.data;

        return result;
      });
    },

    onSuccess: async (result, { onSuccess }) => {
      const { workspace: newWorkspace, addedUsersToTheCloud } = result;

      queryClient.invalidateQueries({
        queryKey: ["user", tenantID],
      });

      /**
       * `addedToTheCloud` is a boolean which if true it means new users were added to the cloud
       *  (those users were not members of the tenant or do not have a gc account before of calling this endpoint)
       */
      if (addedUsersToTheCloud) {
        const tenantUsers = await TenantAPI.getUsersFromTenant().then(
          (data) => {
            return data.data.data;
          }
        );

        queryClient.setQueryData(["tenantUsers", tenantID], tenantUsers);
      }

      setLoading(false);

      queryClient.setQueryData<any>(["workspaces", tenantID], (data: any) => {
        const workspaces = [...data.workspaces, newWorkspace];

        return handleWorkspacesUpdate(workspaces, user);
      });

      setWorkspaceID(newWorkspace.id);
      localStorage.setItem("workspaceID", newWorkspace.id);

      newToastEvent(
        "success",
        `Successfully created ${newWorkspace.Name} workspace`
      );
      callFunctionIfExists(onSuccess);
      navigate(`/clouds/${tenantID}/workspaces/${newWorkspace.id}/home/pages`);
    },
    onError: () => {
      setLoading(false);
    },
  });

  return {
    addWorkspace: addWorkspace.mutate,
    addSubmitting: loading,
    addError: addWorkspace.error ? getErrorMessage(addWorkspace.error) : null,
  };
};

export default useAddWorkspace;
